import { useEffect, useState } from "react";
import client from "../api/client";
import type { FullStub, PayStubPeriod } from "../api/QuikTripClient";
import { fmtDate } from "../lib/util";

function fmt(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function StubDetail({ stub }: { stub: FullStub }) {
  const [showEarnings, setShowEarnings] = useState(false);
  const [showDeductions, setShowDeductions] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      {/* Net pay hero */}
      <div
        className="flex flex-col gap-1 rounded-2xl p-5"
        style={{
          background: "linear-gradient(145deg, #c8102e 0%, #7a0018 100%)",
        }}
      >
        <div className="text-xs tracking-widest text-white/60 uppercase">
          Net Pay
        </div>
        <div className="text-4xl font-black tracking-tight text-white">
          {fmt(stub.netEarnings)}
        </div>
        <div className="mt-1 text-xs text-white/50">
          {stub.depositInfo.disbursmentType} · ••••
          {stub.depositInfo.accountNumber.slice(-4)}
        </div>
        <div className="text-xs text-white/50">
          Check date: {fmtDate(stub.depositInfo.checkDate)}
        </div>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Hours", value: stub.totalHours.toFixed(1) },
          { label: "Gross", value: fmt(stub.grossEarnings) },
          { label: "Deductions", value: fmt(stub.grossDeductions) },
        ].map(({ label, value }) => (
          <div
            key={label}
            className="bg-base-200 rounded-xl px-3 py-3 text-center"
          >
            <div className="text-base-content/50 text-xs tracking-wide uppercase">
              {label}
            </div>
            <div className="mt-1 truncate text-sm font-bold">{value}</div>
          </div>
        ))}
      </div>

      {/* Earnings */}
      <div className="bg-base-200 overflow-hidden rounded-xl">
        <button
          type="button"
          onClick={() => setShowEarnings((v) => !v)}
          className="active:bg-base-300 flex w-full items-center justify-between px-4 py-3 text-left transition-colors"
        >
          <span className="text-sm font-semibold">Earnings</span>
          <span className="text-base-content/40 text-lg leading-none">
            {showEarnings ? "−" : "+"}
          </span>
        </button>
        {showEarnings && (
          <div className="divide-base-300 divide-y">
            {stub.earnings.map((e) => (
              <div
                key={e.description}
                className="flex items-center justify-between px-4 py-2.5"
              >
                <div>
                  <div className="text-sm">{e.description}</div>
                  <div className="text-base-content/50 text-xs">
                    {e.hours}h @ {fmt(e.rate)}
                  </div>
                </div>
                <div className="text-sm font-medium">{fmt(e.gross)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Deductions */}
      <div className="bg-base-200 overflow-hidden rounded-xl">
        <button
          type="button"
          onClick={() => setShowDeductions((v) => !v)}
          className="active:bg-base-300 flex w-full items-center justify-between px-4 py-3 text-left transition-colors"
        >
          <span className="text-sm font-semibold">Deductions</span>
          <span className="text-base-content/40 text-lg leading-none">
            {showDeductions ? "−" : "+"}
          </span>
        </button>
        {showDeductions && (
          <div className="divide-base-300 divide-y">
            {stub.deductions.map((d) => (
              <div
                key={d.description}
                className="flex items-center justify-between px-4 py-2.5"
              >
                <div className="text-sm">{d.description}</div>
                <div className="text-error text-sm font-medium">
                  −{fmt(d.amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* YTD */}
      <div className="bg-base-200 flex items-center justify-between rounded-xl px-4 py-3">
        <span className="text-base-content/50 text-xs tracking-widest uppercase">
          YTD Gross
        </span>
        <span className="text-sm font-bold">
          {fmt(stub.grossYearToDateEarnings)}
        </span>
      </div>
    </div>
  );
}

export function PayPage() {
  const [periods, setPeriods] = useState<PayStubPeriod[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    client
      .getAllPayStubs()
      .then((data) => setPeriods(data))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="loading loading-spinner loading-md text-primary" />
      </div>
    );
  }

  if (error) {
    return <div className="text-error p-6 text-center text-sm">{error}</div>;
  }

  if (periods.length === 0) {
    return (
      <div className="text-base-content/50 p-6 text-center text-sm">
        No pay stubs available.
      </div>
    );
  }

  const period = periods[selectedIdx];
  const stub = period.fullStubs[0] ?? null;

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Period selector */}
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [&::-webkit-scrollbar]:hidden">
        {periods.map((p, i) => (
          <button
            key={p.weekEndDate}
            type="button"
            onClick={() => setSelectedIdx(i)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              i === selectedIdx
                ? "bg-primary text-white"
                : "bg-base-200 text-base-content/60"
            }`}
          >
            {fmtDate(p.weekEndDate)}
          </button>
        ))}
      </div>

      {stub ? (
        <StubDetail stub={stub} />
      ) : (
        <div className="text-base-content/50 py-8 text-center text-sm">
          No stub for this period.
        </div>
      )}
    </div>
  );
}
